import React, { FC, useEffect } from 'react';

import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddRounded } from '@mui/icons-material';

import {
  CreateCategoryResponse,
  useGetAllCategory,
} from '../../api-http/categories';
import { useCreateDraft } from '../../api-http/drafts';
import { AlertBar } from '../../components/AlertBar';
import { formMsg } from '../../configs';
import { useIsUserRole } from '../../util/useIsUserRole';
import { UserRole } from '../../types/user.types';
import { useAuth } from '../../context/auth-context';

interface PresentationFormProps {
  onFinished: () => void;
}

const validationSchema = yup.object({
  title: yup.string().required(formMsg.TITLE_REQUIRED),
});

export const PresentationForm: FC<PresentationFormProps> = ({ onFinished }) => {
  const { switchedOrgId } = useAuth();
  const { isLoading, error, data, mutate } = useCreateDraft();
  const { data: categoryData } = useGetAllCategory();
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      navigate(`/presentations-editor/${data?._id}`);
      onFinished();
    }
  }, [data]);

  const isSuperAdmin = useIsUserRole([UserRole.superAdmin]);

  const formik = useFormik({
    initialValues: {
      title: '',
      category: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      mutate(
        isSuperAdmin && switchedOrgId
          ? {
              name: values.title,
              status: true, // TODO: Get this data from the form creation
              slides: [],
              category: values.category,
              organizationId: switchedOrgId,
            }
          : {
              name: values.title,
              status: true, // TODO: Get this data from the form creation
              slides: [],
              category: values.category,
            },
      );
    },
  });

  return (
    <Box sx={{ pt: 1.5 }}>
      <AlertBar severity="error" msg={error?.msg} />

      <Typography sx={{ color: '#64748b', fontSize: 14, mb: 3 }}>
        Give your presentation a clear name. You can optionally organize it in a
        category.
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2.5}>
          <Box>
            <Typography
              component="label"
              htmlFor="title"
              sx={{
                display: 'block',
                color: '#334155',
                fontSize: 13,
                fontWeight: 700,
                mb: 0.75,
              }}
            >
              Presentation title
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="title"
              name="title"
              placeholder="Enter a presentation title"
              value={formik.values.title}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 44,
                  borderRadius: 2,
                  bgcolor: '#fff',
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              component="label"
              htmlFor="category"
              sx={{
                display: 'block',
                color: '#334155',
                fontSize: 13,
                fontWeight: 700,
                mb: 0.75,
              }}
            >
              Category{' '}
              <Typography
                component="span"
                sx={{ color: '#94a3b8', fontSize: 12, fontWeight: 400 }}
              >
                (optional)
              </Typography>
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                id="category"
                name="category"
                value={formik.values.category}
                title="category"
                onChange={formik.handleChange}
                renderValue={(selected) =>
                  selected
                    ? categoryData?.find(
                        (category) => category._id === selected,
                      )?.name || 'Selected category'
                    : 'No category'
                }
                sx={{
                  minHeight: 44,
                  borderRadius: 2,
                  color: formik.values.category ? '#0f172a' : '#64748b',
                  bgcolor: '#fff',
                }}
              >
                <MenuItem value="">No category</MenuItem>
                {categoryData?.map((categoryName: CreateCategoryResponse) => (
                  <MenuItem key={categoryName?._id} value={categoryName?._id}>
                    {categoryName?.name || ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            justifyContent="flex-end"
            spacing={1.25}
            sx={{ pt: 1 }}
          >
            <Button
              variant="outlined"
              onClick={onFinished}
              disabled={isLoading}
              sx={{
                minWidth: 104,
                height: 42,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                color: '#475569',
                borderColor: '#cbd5e1',
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!formik.values.title.trim() || isLoading}
              variant="contained"
              type="submit"
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AddRounded />
                )
              }
              sx={{
                minWidth: 184,
                height: 42,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: '#0b8fca',
                boxShadow: '0 4px 12px rgba(11, 143, 202, 0.2)',
                '&:hover': { bgcolor: '#087caf' },
              }}
            >
              Create presentation
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};
